import React, { useCallback, useMemo, useRef, useState } from "react";
import {
  useGetRfiQuestionnaireSnapshot,
  useGetRfiScoredSnapshot,
  useGetRiskMethodology,
  usePatchSmeOverride,
  usePublishRunWithValidation,
  useUnpublishRun,
  useAvailableRiskModelsForVqq,
  useSelectRiskModelForVqq,
  usePreviewRiskModelForVqq,
} from "../../../custom-hooks/queries";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { forceRescoreVqq, scoreVqq } from "../../../api/api-methods";
import { getLocationFromProvider } from "../../../utils/addressUtils";
import { useIsOrgRiskScoringEnabled } from "../../../utils/orgFeatureGates";
import { vqqRiskScoringRequested } from "../../../utils/vqqRiskAddOns";
import { isDiligentAdminUserRole, useUserRole, getUserType } from "../../../utils/helperMethods";
import VqqPublishHeader from "./VqqPublishHeader";
import VqqStatsBar from "./VqqStatsBar";
import VqqSectionSidebar from "./VqqSectionSidebar";
import VqqSectionView from "./VqqSectionView";
import RiskOverviewCard from "./RiskOverviewCard";
import VqqFiltersBar, { EMPTY_FILTERS } from "./VqqFiltersBar";
import PublishBar from "./PublishBar";
import RunRiskScoringBanner from "./RunRiskScoringBanner";
import { riskSurfaceEnabled } from "./riskSurface";
import { countFindingSeverities } from "./findingSeverity";
import { useSnapshotPreparingRetry } from "./snapshotPreparing";
import { describePublishProblem } from "./riskScoreDisplay";
import NotificationModal from "../../../components/NotificationModal";
import {
  aggregateSection,
  sectionScoreBand,
  questionSeverityBucket,
  questionCriticality,
} from "./riskAggregates";

const EMPTY_SECTIONS = [];

const buildVendorLine = (rfisDetails) => {
  const location = getLocationFromProvider({
    city: rfisDetails?.location_city,
    state: rfisDetails?.location_state,
    country: rfisDetails?.location_country,
  });
  return [rfisDetails?.vendorName, location].filter(Boolean).join(" · ");
};

const VqqResponsesReadOnly = ({ questionnaireId, rfisDetails }) => {
  // ↓ All hooks below; risk-surface activation is computed AFTER the scored
  // snapshot loads, then a single `enableRiskSurface` boolean gates every
  // risk-related UI element (overview card, filters bar's risk model picker /
  // severity / criticality filters, per-question risk panel, publish bar,
  // SME override controls). For non-Diligent / no-risk-purchased VQQs this
  // collapses the surface down to plain Q&A — per 14033 AC.
  const { data, isLoading, isError, error, refetch } = useGetRfiQuestionnaireSnapshot(
    questionnaireId
  );
  // A missing snapshot is re-ingested on request; keep asking while that runs.
  const { preparing: snapshotPreparing } = useSnapshotPreparingRetry({ isError, error, refetch });

  const userRole = useUserRole();
  const isAdmin = isDiligentAdminUserRole(userRole);
  const isRiskEnabled = useIsOrgRiskScoringEnabled();
  // Only a Diligent VQQ that asked for a risk add-on carries a risk surface here.
  const canSeeRisk = (isAdmin || isRiskEnabled) && vqqRiskScoringRequested(rfisDetails);
  // 14032 AC #17/18 — picker viewer mode: admin gets full controls; sponsor
  // gets a read-only browse-only picker; vendor gets only the badge.
  const viewerKind = isAdmin
    ? "admin"
    : getUserType(userRole) === "sponsor"
      ? "sponsor"
      : "vendor";

  const rfiQuestionnaireGuid = data?.rfi_questionnaire_id;
  // 14033 — Sponsor + vendor see ONLY published runs. Admin sees the latest
  // (which may be a draft / unpublished re-score). Backend honors
  // publishedOnly by walking history to the most recent published run.
  const { data: rawScoredSnapshot } = useGetRfiScoredSnapshot(rfiQuestionnaireGuid, {
    enabled: !!rfiQuestionnaireGuid && canSeeRisk,
    publishedOnly: !isAdmin,
  });
  const { data: methodology } = useGetRiskMethodology({ enabled: canSeeRisk });

  // Plan B: ephemeral model preview. Selecting a model in the picker (sponsor
  // on their own VQQ, or admin on any) computes that model's scores on the fly
  // and lands them here. The published run is never touched. Everything below
  // renders from `scoredSnapshot` (the preview when one is active, else the
  // real published/draft snapshot). Publish / SME / unpublish keep pointing at
  // the REAL run (`rawScoredSnapshot`) so a preview can never be published by
  // accident. Only an explicit "Apply & publish" commits it.
  const [preview, setPreview] = useState(null); // { snapshot, modelId, modelName } | null
  const isPreviewing = !!preview;
  const scoredSnapshot = preview?.snapshot ?? rawScoredSnapshot;
  // Who can commit a preview to the published view: the Diligent admin (any
  // VQQ) and the owning sponsor (their own VQQ, backend enforces ownership).
  const canPublishPreview = isAdmin || viewerKind === "sponsor";

  // 14033 — Activation gate for risk surfaces. Risk overview card, filter
  // chips (Finding Severity / Section Risk Score / Section Criticality /
  // Question Criticality / Risk Model picker), per-question Risk Panel,
  // PublishBar, SmeOverrideControls — all stay hidden when this is false.
  // Mode comes from the snapshot envelope (14031). When there's no snapshot
  // at all (no scoring run), or the resolved run has mode = "None", the
  // VQQ isn't a risk-scoring VQQ and the tab collapses to plain Q&A.
  // Defensive read: never throw if the envelope is missing or partially
  // shaped — a back-end hiccup must not crash the whole tab.
  //
  // Admin backfill exception: when an admin manually runs the engine on a
  // VQQ that wasn't purchased with risk scoring, the snapshot envelope
  // still returns mode="None" because mode is resolved from purchase
  // entitlement, not from whether a scoring run exists. Without this
  // carve-out the page would show the score in the summary widget but
  // hide the risk surface and keep showing the "Run risk scoring" banner
  // even after a successful run. Sponsor/vendor view stays gated by
  // purchase mode so unpurchased VQQs continue to render plain Q&A for
  // them.
  //
  // Sponsors see the surface only once an admin has published the run. Before
  // that the pending banner below is all they get, so no scoring progress or
  // finding counts leak out of the pre-publish stub.
  const snapshotMode = typeof scoredSnapshot?.mode === "string" ? scoredSnapshot.mode : null;
  const enableRiskSurface = riskSurfaceEnabled({ isPreviewing, canSeeRisk, snapshot: scoredSnapshot, isAdmin });

  const scoredRowsByQuestionId = useMemo(() => {
    const map = new Map();
    const rows = scoredSnapshot?.rows;
    if (!Array.isArray(rows)) return map;
    for (const row of rows) {
      if (row?.source_rfi_question_id) {
        map.set(row.source_rfi_question_id, row);
      }
    }
    return map;
  }, [scoredSnapshot]);

  const sections = data?.sections ?? EMPTY_SECTIONS;

  // 14030 — Per-section aggregates feed the sidebar, section accordion, AND
  // the Section Risk Score / Section Criticality filters. We also stash the
  // raw scored rows per section so the filter logic can derive the score band.
  const sectionAggregates = useMemo(() => {
    const aggregatesBySection = new Map();
    const rowsBySection = new Map();
    if (!canSeeRisk) return { aggregatesBySection, rowsBySection };
    for (const section of sections) {
      const rows = (section.questions ?? [])
        .map((q) => scoredRowsByQuestionId.get(q.rfi_question_id))
        .filter(Boolean);
      rowsBySection.set(section.section, rows);
      if (rows.length > 0) {
        aggregatesBySection.set(section.section, aggregateSection(rows, methodology));
      }
    }
    return { aggregatesBySection, rowsBySection };
  }, [sections, scoredRowsByQuestionId, canSeeRisk]);

  const { aggregatesBySection, rowsBySection } = sectionAggregates;

  const totals = useMemo(() => {
    const rows = scoredSnapshot?.rows ?? [];
    let scored = 0;
    let smeScored = 0;
    let autoScored = 0;
    for (const r of rows) {
      if (r?.rubric_impact == null) continue;
      scored += 1;
      // Auto-scored vs SME-scored progress: a row is SME-touched when ANY of
      // sme_reviewer_email / sme_tier / sme_score / sme_review_notes carries a
      // value. Its Finding Severity only changes with sme_tier.
      const smeTouched =
        (r?.sme_reviewer_email && String(r.sme_reviewer_email).trim().length > 0) ||
        (r?.sme_tier && String(r.sme_tier).trim().length > 0) ||
        r?.sme_score != null ||
        (r?.sme_review_notes && String(r.sme_review_notes).trim().length > 0);
      if (smeTouched) smeScored += 1;
      else autoScored += 1;
    }
    return { scored, smeScored, autoScored, ...countFindingSeverities(rows) };
  }, [scoredSnapshot]);

  const [openSet, setOpenSet] = useState(() => new Set());
  const [activeSection, setActiveSection] = useState(null);
  const [filters, setFilters] = useState(EMPTY_FILTERS);
  const sectionRefs = useRef(new Map());

  // Notification modal — surfaces 409s, 422s, and any unhandled mutation
  // errors with a friendly title + the server's reason. Auto-dismisses after
  // 5s on success / informational, sticky until clicked on errors.
  const [notification, setNotification] = useState({
    open: false,
    title: "",
    desc: "",
    error: false,
  });
  const showNotification = useCallback((next) => {
    setNotification({ open: true, error: false, ...next });
  }, []);
  const showErrorNotification = useCallback((title, err, fallback) => {
    const data = err?.response?.data;
    const desc =
      (data && typeof data === "object" && (data.message || data.detail)) ||
      (typeof data === "string" ? data : null) ||
      err?.message ||
      fallback ||
      "An unexpected error occurred. Please try again.";
    setNotification({ open: true, error: true, title, desc });
  }, []);

  // 14031 — Publish workflow mutations + admin override mutation. All three
  // are no-ops when called by a non-admin (the FE doesn't render the controls
  // for non-admins anyway, but the hooks are safe to instantiate).
  const smeOverrideMutation = usePatchSmeOverride();
  const publishMutation = usePublishRunWithValidation();
  const unpublishMutation = useUnpublishRun();
  const [publishValidation, setPublishValidation] = useState(null);

  // 14032 — Active risk model picker. The list resolves the (template, sponsor)
  // pair server-side from the VQQ row; the FE just renders. Selecting a model
  // stages it on the VQQ (no rescore yet) and exposes a Rescore button.
  // Wrapped in defensive checks: if the picker endpoint fails or returns null,
  // the picker silently falls back to the read-only stock-model label.
  const queryClient = useQueryClient();
  // 14032 AC #17 — sponsors with risk entitlement also fetch the model list
  // so the picker can render their browse-only view. Vendor doesn't need it
  // (just gets the badge name from the snapshot).
  const { data: availableModelsRaw } = useAvailableRiskModelsForVqq(
    rfiQuestionnaireGuid,
    { enabled: !!rfiQuestionnaireGuid && canSeeRisk && viewerKind !== "vendor" }
  );
  const availableModels = Array.isArray(availableModelsRaw) ? availableModelsRaw : null;
  const selectModelMutation = useSelectRiskModelForVqq();
  const previewModelMutation = usePreviewRiskModelForVqq();
  const rescoreMutation = useMutation({
    mutationFn: ({ vqqGuid }) => forceRescoreVqq(vqqGuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rfiScoredSnapshot"] });
      queryClient.invalidateQueries({ queryKey: ["riskSummaries"] });
      queryClient.invalidateQueries({ queryKey: ["availableRiskModels"] });
      showNotification({
        title: "Rescore complete",
        desc: "The risk score has been recalculated with the active model. Review and Publish to share with sponsor and vendor.",
      });
    },
    onError: (err) => {
      const status = err?.response?.status;
      if (status === 409) {
        showErrorNotification(
          "Can't rescore a published VQQ",
          err,
          "Unpublish the current snapshot before re-running the scoring engine."
        );
      } else {
        showErrorNotification("Rescore failed", err);
      }
    },
  });

  // 14032 — Auto-rescore on model switch. Pipeline:
  //   1. Stage the selection on the VQQ row.
  //   2. If the currently-resolved run is published (is_locked=true), unpublish
  //      it first — otherwise the rescore call would 409 against the hard lock
  //      and the user would be stuck.
  //   3. Force-rescore. Engine runs against the merged rubric (Diligent base +
  //      custom overrides) and lands a fresh draft run (is_published=false,
  //      is_locked=false). Sponsor / vendor view falls back to the pending
  //      banner during this gap — admin re-publishes when they're done.
  // The explicit "Rescore with this model" button is kept for cases where the
  // automatic chain fails partway and the admin wants to retry manually.
  const handleSelectModel = useCallback(
    async (customRiskModelId) => {
      if (!rfiQuestionnaireGuid) return;
      try {
        await selectModelMutation.mutateAsync({
          rfiQuestionnaireId: rfiQuestionnaireGuid,
          customRiskModelId,
        });
      } catch (err) {
        showErrorNotification(
          "Couldn't switch risk model",
          err,
          "The selected model isn't available for this VQQ's template + sponsor scope."
        );
        return;
      }

      const lockedRunId = scoredSnapshot?.is_published
        ? scoredSnapshot?.scoring_run_id
        : null;
      if (lockedRunId) {
        try {
          await unpublishMutation.mutateAsync({ scoringRunId: lockedRunId });
        } catch (err) {
          showErrorNotification(
            "Couldn't unpublish before rescoring",
            err,
            "The current snapshot is published but the auto-unpublish step failed. Unpublish manually, then re-pick the model."
          );
          return;
        }
      }

      // Rescore errors are already surfaced by rescoreMutation.onError.
      await rescoreMutation.mutateAsync({ vqqGuid: rfiQuestionnaireGuid }).catch(() => {});
    },
    [rfiQuestionnaireGuid, selectModelMutation, unpublishMutation, rescoreMutation, scoredSnapshot, showErrorNotification]
  );

  // Plan B: picker selection on an already-scored VQQ previews the model
  // ephemerally (persists nothing). Both the admin and the owning sponsor can
  // then commit the preview to the published view (Apply & publish) or discard
  // it (Reset).
  const handlePreviewModel = useCallback(
    async (customRiskModelId, modelName) => {
      if (!rfiQuestionnaireGuid) return;
      try {
        const snap = await previewModelMutation.mutateAsync({
          rfiQuestionnaireId: rfiQuestionnaireGuid,
          customRiskModelId: customRiskModelId ?? null,
        });
        setPreview({
          snapshot: snap,
          modelId: customRiskModelId ?? null,
          modelName: modelName || snap?.preview_model_name || "Diligent Risk Model",
        });
      } catch (err) {
        showErrorNotification(
          "Couldn't preview that model",
          err,
          "The selected model may not be available for this VQQ's template and sponsor scope."
        );
      }
    },
    [rfiQuestionnaireGuid, previewModelMutation, showErrorNotification]
  );

  // Exit / Reset: drop the preview overlay and fall back to the real
  // published/draft snapshot. Nothing was persisted, so there's nothing to undo.
  const handleExitPreview = useCallback(() => setPreview(null), []);

  // Make the previewed model the published view (admin on any VQQ, or the
  // owning sponsor on their own). Stage the selection, unpublish the live run
  // if needed (so the force-rescore's delete-by-key clears the hard lock),
  // rescore for real, then publish the fresh run. Once the rescore lands, the
  // real snapshot reflects the model, so we drop the preview overlay regardless
  // of the publish outcome.
  const handleApplyPreview = useCallback(async () => {
    if (!rfiQuestionnaireGuid || !preview) return;
    const modelId = preview.modelId ?? null;
    const mode = preview.snapshot?.mode;
    try {
      await selectModelMutation.mutateAsync({
        rfiQuestionnaireId: rfiQuestionnaireGuid,
        customRiskModelId: modelId,
      });

      const lockedRunId = rawScoredSnapshot?.is_published
        ? rawScoredSnapshot?.scoring_run_id
        : null;
      if (lockedRunId) {
        await unpublishMutation.mutateAsync({ scoringRunId: lockedRunId });
      }

      const rescore = await rescoreMutation.mutateAsync({ vqqGuid: rfiQuestionnaireGuid });
      // The real run now reflects the model, so drop the preview overlay and
      // the page shows the persisted draft with the PublishBar.
      setPreview(null);

      const newRunId = rescore?.data?.scoring_run_id;
      if (!newRunId) return;

      const requireGapText = mode === "SmeOnly" || mode === "Both";
      const result = await publishMutation.mutateAsync({
        scoringRunId: newRunId,
        requireGapText,
        mode,
      });
      if (result?.ok) {
        setPublishValidation(null);
        showNotification({
          title: "Model applied & published",
          desc: "The new risk model is now the published view. Sponsor and vendor see it on their grids and on the VQQ Details page.",
        });
      } else if (result?.status === 422) {
        setPublishValidation(result.validation);
        const problem = describePublishProblem(result.validation, mode, { rescored: true });
        showErrorNotification(problem.title, null, problem.desc);
      }
    } catch (err) {
      // selectModel / unpublish / rescore each surface their own errors
      // (e.g. rescore 409). Leave whatever state resulted; the admin can retry.
    }
  }, [
    rfiQuestionnaireGuid,
    preview,
    selectModelMutation,
    unpublishMutation,
    rescoreMutation,
    publishMutation,
    rawScoredSnapshot,
    showNotification,
    showErrorNotification,
  ]);

  // First-time scoring path for VQQs that have never been run through the
  // engine (historical RFIs pre-14030). Hits the idempotent /score endpoint
  // rather than /score/force — same engine, no cache to bust on first run.
  const scoreMutation = useMutation({
    mutationFn: ({ vqqGuid }) => scoreVqq(vqqGuid),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["rfiScoredSnapshot"] });
      queryClient.invalidateQueries({ queryKey: ["riskSummaries"] });
      queryClient.invalidateQueries({ queryKey: ["availableRiskModels"] });
      showNotification({
        title: "Risk scoring complete",
        desc: "The risk score has been generated. Review the findings and Publish to share with sponsor and vendor.",
      });
    },
    onError: (err) => {
      showErrorNotification(
        "Couldn't run risk scoring",
        err,
        "The risk engine couldn't score this VQQ. Confirm a rubric exists for the template and try again."
      );
    },
  });

  const handleScoreNow = useCallback(async () => {
    if (!rfiQuestionnaireGuid) return;
    await scoreMutation.mutateAsync({ vqqGuid: rfiQuestionnaireGuid }).catch(() => {});
  }, [rfiQuestionnaireGuid, scoreMutation]);

  const handleSmeSave = useCallback(
    async ({ snapshotId, payload }) => {
      if (!snapshotId) return;
      try {
        await smeOverrideMutation.mutateAsync({ snapshotId, payload });
      } catch (err) {
        const status = err?.response?.status;
        if (status === 409) {
          showErrorNotification(
            "Can't edit a published finding",
            err,
            "This scoring run is published and locked. Unpublish first to edit per-finding overrides."
          );
        } else {
          showErrorNotification("Couldn't save SME review", err);
        }
        throw err;
      }
    },
    [smeOverrideMutation, showErrorNotification]
  );

  // Publish / unpublish always act on the REAL run, never an active preview.
  // A preview is ephemeral and has no persisted run id to publish.
  const handlePublish = useCallback(async () => {
    if (!rawScoredSnapshot?.scoring_run_id) return;
    const requireGapText =
      rawScoredSnapshot.mode === "SmeOnly" || rawScoredSnapshot.mode === "Both";
    try {
      const result = await publishMutation.mutateAsync({
        scoringRunId: rawScoredSnapshot.scoring_run_id,
        requireGapText,
        mode: rawScoredSnapshot.mode,
      });
      if (result?.ok) {
        setPublishValidation(null);
        showNotification({
          title: "Risk Score published",
          desc: "Sponsor and vendor can now see the published snapshot on their grids and on the VQQ Details page.",
        });
      } else if (result?.status === 422) {
        setPublishValidation(result.validation);
        const problem = describePublishProblem(result.validation, rawScoredSnapshot.mode);
        showErrorNotification(problem.title, null, problem.desc);
      }
    } catch (err) {
      showErrorNotification("Publish failed", err);
    }
  }, [publishMutation, rawScoredSnapshot, showErrorNotification, showNotification]);

  const handleUnpublish = useCallback(async () => {
    if (!rawScoredSnapshot?.scoring_run_id) return;
    try {
      await unpublishMutation.mutateAsync({ scoringRunId: rawScoredSnapshot.scoring_run_id });
      setPublishValidation(null);
      showNotification({
        title: "Unpublished",
        desc: "The snapshot has been retracted. Sponsor and vendor will see the pending-review banner until you re-publish.",
      });
    } catch (err) {
      showErrorNotification("Unpublish failed", err);
    }
  }, [unpublishMutation, rawScoredSnapshot, showErrorNotification, showNotification]);

  // Snapshot ids that block publish — server-supplied so FE highlight matches
  // what the publish endpoint would reject on. Falls back to live validation
  // result if the user just attempted publish.
  const missingGapTextIds = useMemo(() => {
    const fromServer = scoredSnapshot?.snapshot_ids_missing_gap_text;
    const fromValidation = publishValidation?.snapshot_ids_missing_gap_text;
    const arr = Array.isArray(fromValidation) ? fromValidation : fromServer;
    return new Set(Array.isArray(arr) ? arr : []);
  }, [scoredSnapshot, publishValidation]);

  /**
   * Section + question filtering driven by the filter bar.
   *
   * Steps per section:
   *   1. Drop section if Section Risk Score doesn't match (active filter only).
   *   2. Drop section if Section Criticality doesn't match (active filter only).
   *   3. Drop questions inside the section that fail Finding Severity, Question
   *      Criticality, or the free-text search.
   *   4. Drop the whole section if its question list ends up empty.
   *
   * Empty filter Set = "any value passes" — that's the no-filter state.
   */
  const filteredSections = useMemo(() => {
    if (!sections.length) return [];

    const wantSeverity = filters.severity.size > 0 ? filters.severity : null;
    const wantSectionScore = filters.sectionScore.size > 0 ? filters.sectionScore : null;
    const wantSectionCrit = filters.sectionCriticality.size > 0 ? filters.sectionCriticality : null;
    const wantQuestionCrit = filters.questionCriticality.size > 0 ? filters.questionCriticality : null;
    const search = filters.search.trim().toLowerCase();

    const out = [];
    for (const section of sections) {
      const agg = aggregatesBySection.get(section.section);
      const rows = rowsBySection.get(section.section) ?? [];

      if (wantSectionScore) {
        const band = sectionScoreBand(rows, methodology);
        if (!band || !wantSectionScore.has(band)) continue;
      }
      if (wantSectionCrit) {
        if (!agg?.criticality || !wantSectionCrit.has(agg.criticality)) continue;
      }

      const filteredQuestions = [];
      for (const q of section.questions ?? []) {
        const scoredRow = scoredRowsByQuestionId.get(q.rfi_question_id);

        if (wantSeverity) {
          const bucket = questionSeverityBucket(scoredRow, methodology);
          if (!wantSeverity.has(bucket)) continue;
        }
        if (wantQuestionCrit) {
          const crit = questionCriticality(scoredRow, methodology);
          if (!crit || !wantQuestionCrit.has(crit)) continue;
        }
        if (search) {
          const haystack = [
            q.question_code,
            q.question_text,
            q.primary_answer,
            q.comments,
            scoredRow?.response_label,
          ]
            .filter(Boolean)
            .join(" ")
            .toLowerCase();
          if (!haystack.includes(search)) continue;
        }

        filteredQuestions.push(q);
      }

      if (filteredQuestions.length === 0) continue;
      out.push({ ...section, questions: filteredQuestions });
    }
    return out;
  }, [sections, filters, aggregatesBySection, rowsBySection, scoredRowsByQuestionId]);

  // The filter bar can hide entire sections. When a filter is active, open
  // every surviving section automatically — saves the user 10 clicks to find
  // their matches. We don't auto-close on empty filters: the user might have
  // opened sections manually and we shouldn't claw that back.
  const hasFilter = useMemo(
    () =>
      filters.severity.size > 0 ||
      filters.sectionScore.size > 0 ||
      filters.sectionCriticality.size > 0 ||
      filters.questionCriticality.size > 0 ||
      filters.search.trim().length > 0,
    [filters]
  );

  const effectiveOpenSet = useMemo(() => {
    if (!hasFilter) return openSet;
    const all = new Set(openSet);
    for (const s of filteredSections) all.add(s.section);
    return all;
  }, [hasFilter, openSet, filteredSections]);

  const registerRef = useCallback((sectionId, node) => {
    if (node) sectionRefs.current.set(sectionId, node);
    else sectionRefs.current.delete(sectionId);
  }, []);

  const handleToggle = useCallback((sectionId) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      if (next.has(sectionId)) next.delete(sectionId);
      else next.add(sectionId);
      return next;
    });
    setActiveSection(sectionId);
  }, []);

  const handleSidebarSelect = useCallback((sectionId) => {
    setOpenSet((prev) => {
      const next = new Set(prev);
      next.add(sectionId);
      return next;
    });
    setActiveSection(sectionId);
    const node = sectionRefs.current.get(sectionId);
    if (node) {
      node.scrollIntoView({ behavior: "smooth", block: "start" });
    }
  }, []);

  const total = data?.stats?.total_questions ?? 0;
  const complete = data?.stats?.complete_count ?? 0;
  const vendorCompletedPct = total > 0 ? (complete / total) * 100 : 0;
  // Bar shows the share of in-scope questions whose finding came from a
  // Diligent SME touch (override / gap text / regulatory cite) vs the
  // share still showing the auto-generated rubric result. Total = auto +
  // SME; the bar is split horizontally between them.
  const autoScoredPct = total > 0 ? (totals.autoScored / total) * 100 : 0;
  const smeScoredPct = total > 0 ? (totals.smeScored / total) * 100 : 0;
  const vendorLine = buildVendorLine(rfisDetails);
  const completedAt = data?.release_date ?? rfisDetails?.completedOn;
  const lastPublishBy = rfisDetails?.lastPublishBy || null;

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-16 text-gray-500">
        Loading questionnaire&hellip;
      </div>
    );
  }

  if (isError && snapshotPreparing) {
    return (
      <div
        role="status"
        className="bg-indigo-50 border border-indigo-200 rounded p-4 text-sm text-indigo-800 mx-4 my-4"
      >
        <p className="font-medium">Preparing the responses for this VQQ.</p>
        <p className="mt-1 text-indigo-700">
          The questionnaire is being loaded from HyperComply. This page refreshes on its own.
        </p>
      </div>
    );
  }

  if (isError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded p-4 text-sm text-red-700 mx-4 my-4">
        <p className="font-medium">Unable to load the questionnaire snapshot.</p>
        <p className="mt-1 text-red-600">
          {error?.response?.data?.message || error?.message || "Please try again."}
        </p>
        <button
          type="button"
          onClick={() => refetch()}
          className="mt-3 inline-flex items-center px-3 py-1.5 rounded border border-red-300 bg-white text-red-700 hover:bg-red-100 text-xs font-medium"
        >
          Retry
        </button>
      </div>
    );
  }

  if (!data) return null;

  // 14031 AC #5 + 14033 AC #2 — sponsor/vendor see this banner when risk
  // scoring is requested for the VQQ but the Diligent admin hasn't published
  // a snapshot yet. The BE returns a stub envelope with is_published=false
  // and a resolved mode (non-"None" means risk was requested). Admin never
  // sees the banner — they see the live state via PublishBar.
  const showPendingReviewBanner =
    !isAdmin &&
    !isPreviewing &&
    canSeeRisk &&
    !!rawScoredSnapshot &&
    rawScoredSnapshot.is_published === false &&
    !!rawScoredSnapshot.mode &&
    rawScoredSnapshot.mode !== "None";

  const isApplyingPreview =
    selectModelMutation.isPending ||
    unpublishMutation.isPending ||
    rescoreMutation.isPending ||
    publishMutation.isPending;

  return (
    <div className="px-4 py-4 space-y-3">
      {isPreviewing ? (
        <div
          role="status"
          className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 flex items-start gap-3"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-5 w-5 text-amber-600 mt-0.5 shrink-0">
            <path fillRule="evenodd" d="M9.401 3.003c1.155-2 4.043-2 5.197 0l7.355 12.748c1.154 2-.29 4.5-2.599 4.5H4.645c-2.309 0-3.752-2.5-2.598-4.5L9.4 3.003zM12 8.25a.75.75 0 01.75.75v3.5a.75.75 0 01-1.5 0V9a.75.75 0 01.75-.75zm0 8.25a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd" />
          </svg>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-amber-900">
              Previewing{" "}
              <span className="font-bold">{preview?.modelName || "Diligent Risk Model"}</span>
              {" "}(not published)
            </p>
            <p className="mt-0.5 text-[13px] text-amber-800/90">
              {canPublishPreview
                ? "These numbers are a live preview only. Nothing is saved and the published view is unchanged. Apply & publish to make this the official view the vendor sees, or reset to discard."
                : "These numbers are a live preview only. The published view the vendor sees is unchanged."}
            </p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <button
              type="button"
              onClick={handleExitPreview}
              disabled={isApplyingPreview}
              className="rounded-md border border-amber-300 bg-white px-3 py-1.5 text-[13px] font-medium text-amber-800 hover:bg-amber-100 disabled:opacity-50"
            >
              {canPublishPreview ? "Reset" : "Exit preview"}
            </button>
            {canPublishPreview ? (
              <button
                type="button"
                onClick={handleApplyPreview}
                disabled={isApplyingPreview}
                className="rounded-md bg-amber-600 px-3 py-1.5 text-[13px] font-semibold text-white hover:bg-amber-700 disabled:opacity-60"
              >
                {isApplyingPreview ? "Applying…" : "Apply & publish"}
              </button>
            ) : null}
          </div>
        </div>
      ) : null}

      {showPendingReviewBanner ? (
        <div
          role="status"
          className="rounded-md border border-blue-200 bg-blue-50 px-4 py-3 flex items-start gap-3"
        >
          <svg viewBox="0 0 20 20" fill="currentColor" aria-hidden="true" className="h-5 w-5 text-blue-600 mt-0.5 shrink-0">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zm-1 9a1 1 0 01-1-1v-4a1 1 0 112 0v4a1 1 0 01-1 1z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-blue-900">
            <p className="font-semibold">Risk analysis pending Diligent admin review</p>
            <p className="mt-0.5 text-blue-800/90">
              The vendor&apos;s responses have been received. Risk Score, finding severities,
              and section breakdowns will appear here after a Diligent admin reviews and
              publishes the analysis.
            </p>
          </div>
        </div>
      ) : null}

      {isAdmin &&
      !enableRiskSurface &&
      Array.isArray(availableModels) &&
      availableModels.length > 0 ? (
        <RunRiskScoringBanner
          availableModels={availableModels}
          viewerKind={viewerKind}
          onSelectModel={handleSelectModel}
          onScoreNow={handleScoreNow}
          isScoring={
            scoreMutation.isPending ||
            selectModelMutation.isPending
          }
        />
      ) : null}

      {isAdmin && enableRiskSurface && !isPreviewing && rawScoredSnapshot ? (
        <PublishBar
          mode={
            // Backfill: a manual scoring run on a non-purchased VQQ carries
            // mode="None" from the envelope (mode = purchase entitlement).
            // Fall back to AutoScoreOnly so the publish CTA renders instead
            // of PublishBar's "no workflow applies" message.
            (snapshotMode && snapshotMode !== "None")
              ? snapshotMode
              : "AutoScoreOnly"
          }
          isPublished={!!rawScoredSnapshot.is_published}
          publishedAt={rawScoredSnapshot.published_at}
          publishedBy={rawScoredSnapshot.published_by}
          hasUnpublishedChanges={!!rawScoredSnapshot.has_unpublished_changes}
          missingGapTextCount={
            (rawScoredSnapshot.snapshot_ids_missing_gap_text || []).length
          }
          unscoredCount={rawScoredSnapshot.unscored_question_count ?? totals.notScored}
          isPublishing={publishMutation.isPending}
          isUnpublishing={unpublishMutation.isPending}
          onPublish={handlePublish}
          onUnpublish={handleUnpublish}
        />
      ) : null}

      {enableRiskSurface ? (
        <RiskOverviewCard
          scoredSnapshot={scoredSnapshot}
          methodology={methodology}
          templateName={rfisDetails?.name}
        />
      ) : null}

      {enableRiskSurface ? (
        <VqqFiltersBar
          riskModelName={scoredSnapshot?.control_catalog_used || rfisDetails?.name}
          filters={filters}
          onChange={setFilters}
          methodology={methodology}
          isAdmin={isAdmin}
          viewerKind={viewerKind}
          models={availableModels}
          // Plan B: selecting a model previews it ephemerally (sponsor or
          // admin). The preview banner above carries Apply & publish / Reset.
          onSelectModel={handlePreviewModel}
          previewModelId={isPreviewing ? preview.modelId : undefined}
          isPreviewing={isPreviewing}
          isRescoring={previewModelMutation.isPending || isApplyingPreview}
        />
      ) : null}

      <VqqPublishHeader
        title={data.name || rfisDetails?.name || "Questionnaire"}
        vendorLine={vendorLine}
        lastPublishBy={lastPublishBy}
        lastPublishAt={completedAt}
        vendorCompletedPct={vendorCompletedPct}
        autoScoredPct={enableRiskSurface ? autoScoredPct : 0}
        smeScoredPct={enableRiskSurface ? smeScoredPct : 0}
        showScoringProgress={enableRiskSurface}
      />

      <VqqStatsBar
        totalQuestions={total}
        completeCount={complete}
        completedAt={completedAt}
        autoScoredCount={enableRiskSurface ? totals.autoScored : null}
        smeScoredCount={enableRiskSurface ? totals.smeScored : null}
        compliantCount={enableRiskSurface ? totals.compliant : null}
        criticalCount={enableRiskSurface ? totals.critical : null}
        highCount={enableRiskSurface ? totals.high : null}
        mediumCount={enableRiskSurface ? totals.medium : null}
        lowCount={enableRiskSurface ? totals.low : null}
        notScoredCount={enableRiskSurface ? totals.notScored : null}
        // 14033 AC #11/12 — surface "Showing X of Y" + Clear filters in the
        // stats bar when any filter is active. Visible question count =
        // sum of filtered sections' question_count.
        showingCount={
          hasFilter
            ? filteredSections.reduce(
                (acc, s) => acc + (s.questions?.length ?? 0),
                0,
              )
            : null
        }
        hasActiveFilters={hasFilter}
        onClearFilters={() => setFilters(EMPTY_FILTERS)}
      />

      <div className="flex gap-6 items-start pt-2">
        <VqqSectionSidebar
          sections={filteredSections}
          activeSection={activeSection}
          onSelect={handleSidebarSelect}
          aggregatesBySection={enableRiskSurface ? aggregatesBySection : null}
        />
        <div className="flex-1 min-w-0 space-y-3">
          <VqqSectionView
            sections={filteredSections}
            openSet={effectiveOpenSet}
            onToggle={handleToggle}
            registerRef={registerRef}
            scoredRowsByQuestionId={enableRiskSurface ? scoredRowsByQuestionId : null}
            methodology={enableRiskSurface ? methodology : null}
            aggregatesBySection={enableRiskSurface ? aggregatesBySection : null}
            isAdmin={isAdmin && enableRiskSurface && !isPreviewing}
            mode={enableRiskSurface ? scoredSnapshot?.mode : null}
            missingGapTextIds={enableRiskSurface && !isPreviewing ? missingGapTextIds : null}
            onSmeSave={enableRiskSurface && !isPreviewing ? handleSmeSave : null}
          />
          {filteredSections.length === 0 && hasFilter ? (
            <div className="rounded border border-gray-200 bg-white p-6 text-sm text-gray-500">
              No questions match the current filters.
            </div>
          ) : null}
        </div>
      </div>

      <NotificationModal
        open={notification.open}
        setOpen={(open) => setNotification((prev) => ({ ...prev, open }))}
        title={notification.title}
        desc={notification.desc}
        error={notification.error}
      />
    </div>
  );
};

export default VqqResponsesReadOnly;
